import * as React from 'react'
import { useParams } from 'react-router-dom'
import { ConfigurationType } from '@/app/constants/configuration/configurationType'

import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  QUERY_KEY_SETTINGS_SCANNER,
  ScannerSettingsModel,
  useMutationScannerSettings,
  useQueryScannerSettings,
} from '@/lib/api/company/configuration/scanner'
import { ScannerForm } from './ScannerForm'

// Parytet z sekcją "Skaner" w Ustawieniach podstawowych WPF, bez jednego pola:
// "Pokazuj pełny obraz tarczki podczas importu" steruje tam kadrowaniem zapisywanego
// obrazu (1450 px vs 800 px), a w webie odpowiednik jest przełącznikiem podglądu.
export const ScannerPageContent = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { t } = useLocalize()

  const diffLabels = React.useMemo<Record<string, string>>(
    () => ({
      scanBrightness: t('configuration-scanner.brightness'),
      showScannerDialog: t('configuration-scanner.show-dialog'),
    }),
    [t],
  )

  const query = useQueryScannerSettings({ companyId })
  const mutation = useMutationScannerSettings(companyId)
  const lastQueryData = React.useRef<ScannerSettingsModel | null>(null)
  const [state, setState] = React.useState<ScannerSettingsModel | null>(null)

  React.useEffect(() => {
    if (!query.data) return
    setState({ ...query.data })
    lastQueryData.current = query.data
  }, [query.data])

  const isDirty = JSON.stringify(lastQueryData.current) !== JSON.stringify(state)

  const handleReset = () => setState(lastQueryData.current ? { ...lastQueryData.current } : null)
  const handleSave = () => state && mutation.mutate(state)

  return (
    <SettingsPageWrapper
      isReady={!!state}
      isPending={query.isFetching || mutation.isPending}
      isError={query.isError}
      handleSave={handleSave}
      handleReset={handleReset}
      restore={{
        configurationType: ConfigurationType.Scanner,
        tabLabelKey: 'configuration-menu.scanner',
        queryKeys: [QUERY_KEY_SETTINGS_SCANNER],
        serverData: query.data,
        withDefaults: true,
      }}
      isDirty={isDirty}
      errorMsg={t('configuration-scanner.load-error')}
      globalErrorMsg={t('configuration-scanner.load-error')}
      diffBefore={lastQueryData.current as Record<string, unknown>}
      diffAfter={state as Record<string, unknown>}
      diffLabels={diffLabels}
    >
      {state && (
        <ScannerForm isSaving={mutation.isPending} state={state} setState={setState} />
      )}
    </SettingsPageWrapper>
  )
}
